"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";

const stages = [
  {
    id: 1,
    key: "Identify",
    tagline: "Know your attack surface",
    description:
      "We audit every asset, user, and access point across your sites — mapping vulnerabilities before attackers do.",
    color: "#3B82F6",
    items: ["Asset discovery & inventory", "Risk assessment", "Compliance gap analysis"],
  },
  {
    id: 2,
    key: "Protect",
    tagline: "Harden every layer",
    description:
      "Multi-layered defences: endpoint protection, firewall policies, MFA, patch management, and user awareness training.",
    color: "#22D3EE",
    items: ["Endpoint & email security", "Firewall & network hardening", "MFA & identity management"],
  },
  {
    id: 3,
    key: "Detect",
    tagline: "24/7 threat visibility",
    description:
      "Real-time monitoring, SIEM alerting, and behavioural analytics — threats surface in minutes, not months.",
    color: "#60A5FA",
    items: ["SIEM & log monitoring", "Intrusion detection", "Dark web monitoring"],
  },
  {
    id: 4,
    key: "Respond",
    tagline: "Act before damage spreads",
    description:
      "Defined incident response playbooks and a dedicated team ready to contain, investigate, and neutralise any incident.",
    color: "#F87171",
    items: ["Incident response plans", "Threat containment", "Forensic investigation"],
  },
  {
    id: 5,
    key: "Recover",
    tagline: "Back to normal, fast",
    description:
      "Tested backup and recovery procedures ensure minimal downtime — your data, your operations, restored.",
    color: "#A78BFA",
    items: ["Business continuity planning", "Backup & DR testing", "Post-incident review"],
  },
];

// Pre-computed orbiting dot positions to avoid SSR/client hydration mismatch
const orbitingDots = [0, 72, 144, 216, 288]?.map((angle) => {
  const rad = (angle * Math.PI) / 180;
  return {
    cx: parseFloat((100 + 80 * Math.cos(rad))?.toFixed(6)),
    cy: parseFloat((105 + 80 * Math.sin(rad))?.toFixed(6)),
  };
});

export default function CyberSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeStage, setActiveStage] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % stages?.length);
    }, 3200);
    return () => clearInterval(interval);
  }, [visible]);

  const active = stages?.[activeStage];

  return (
    <section
      ref={sectionRef}
      className="pt-10 pb-16 sm:pt-10 sm:pb-24 lg:pt-10 lg:pb-32 relative overflow-hidden"
      style={{ background: "#070B14" }}
    >
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full"
          style={{
            background: "radial-gradient(circle at 80% 10%, rgba(37,99,235,0.07) 0%, transparent 60%)",
            filter: "blur(40px)",
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle at 20% 90%, rgba(34,211,238,0.05) 0%, transparent 60%)",
            filter: "blur(40px)",
          }}
        />
        {/* Scan line */}
        <div
          className="scan-line absolute left-0 right-0 h-px pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.4), transparent)" }}
        />
      </div>
      <div className="absolute inset-0 pointer-events-none grid-overlay" />
      <div className="relative z-10 max-w-container mx-auto px-4 sm:px-6 w-full">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-6"
            style={{ border: "1px solid rgba(59,130,246,0.3)", background: "rgba(37,99,235,0.08)" }}
          >
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#3B82F6" }} />
            <span className="text-[11px] font-heading font-600 tracking-widest uppercase gradient-text">
              NIST Cybersecurity Framework
            </span>
          </div>
          <h2
            className="font-heading font-700 leading-tight mb-4"
            style={{ fontSize: "clamp(28px, 3.5vw, 48px)", color: "#F8FAFC" }}
          >
            Cybersecurity that covers
            <br />
            <span className="gradient-text">every stage of the threat lifecycle.</span>
          </h2>
          <p className="text-[16px] font-body max-w-[520px] mx-auto" style={{ color: "rgba(148,163,184,0.8)" }}>
            We don&apos;t just install antivirus. Our NIST-aligned security strategy protects your business end-to-end.
          </p>
        </div>

        {/* 5-step framework */}
        <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16 w-full">

          {/* Step selector */}
          <div className="w-full lg:w-auto lg:shrink-0 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-1 gap-2">
            {stages?.map((stage, i) => (
              <button
                key={stage?.key}
                onClick={() => setActiveStage(i)}
                className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition-all duration-300 text-left"
                style={{
                  background: i === activeStage ? `${stage?.color}15` : "rgba(255,255,255,0.03)",
                  border: i === activeStage ? `1px solid ${stage?.color}40` : "1px solid rgba(255,255,255,0.06)",
                  minWidth: "0",
                }}
              >
                <div
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[11px] sm:text-[12px] font-heading font-700 shrink-0 transition-all duration-300"
                  style={{
                    background: i === activeStage ? stage?.color : "rgba(255,255,255,0.06)",
                    color: i === activeStage ? "white" : "#94A3B8",
                  }}
                >
                  {i + 1}
                </div>
                <span
                  className="font-heading font-600 text-[12px] sm:text-[14px] transition-colors duration-200 hidden sm:block"
                  style={{ color: i === activeStage ? stage?.color : "#94A3B8" }}
                >
                  {stage?.key}
                </span>
              </button>
            ))}
          </div>

          {/* Active stage detail */}
          <div className="flex-1 min-w-0">
            <div
              className="rounded-card-lg p-5 sm:p-8 lg:p-10 transition-all duration-500"
              key={activeStage}
              style={{
                background: "rgba(11,18,32,0.8)",
                border: `1px solid ${active?.color}30`,
                boxShadow: `0 0 60px ${active?.color}10`,
                backdropFilter: "blur(12px)",
              }}
            >
              {/* Stage badge */}
              <div
                className="inline-block px-3 py-1 rounded-pill text-[12px] font-heading font-600 mb-4"
                style={{ background: `${active?.color}20`, color: active?.color }}
              >
                Stage {activeStage + 1} of 5 — {active?.key}
              </div>

              <h3
                className="font-heading font-700 leading-tight mb-2"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", color: "#F8FAFC" }}
              >
                {active?.key}
              </h3>
              <p className="font-heading font-500 text-[15px] mb-4" style={{ color: active?.color }}>
                {active?.tagline}
              </p>
              <p className="text-[15px] font-body leading-relaxed mb-8" style={{ color: "rgba(203,213,225,0.7)" }}>
                {active?.description}
              </p>

              <ul className="flex flex-col gap-3 mb-8">
                {active?.items?.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `${active?.color}20` }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={active?.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                    <span className="text-[14px] font-body" style={{ color: "rgba(203,213,225,0.75)" }}>{item}</span>
                  </li>
                ))}
              </ul>

              {/* Progress dots */}
              <div className="flex items-center gap-2 mb-6">
                {stages?.map((s, i) => (
                  <button
                    key={s?.key}
                    onClick={() => setActiveStage(i)}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === activeStage ? "32px" : "8px",
                      background: i === activeStage ? active?.color : "rgba(255,255,255,0.2)",
                    }}
                    aria-label={`View ${s?.key} stage`}
                  />
                ))}
              </div>

              <Link
                href="/cyber-security"
                className="inline-flex items-center gap-2 px-6 py-3 text-[14px] font-heading font-600 btn-primary"
              >
                Explore Cyber Security Services
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </div>
          </div>

          {/* Right: Shield visual */}
          <div className="hidden xl:block shrink-0 w-[220px]">
            <svg viewBox="0 0 200 220" className="w-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="shieldGrad" cx="50%" cy="40%" r="60%">
                  <stop offset="0%" stopColor="rgba(59,130,246,0.3)"/>
                  <stop offset="100%" stopColor="rgba(37,99,235,0.05)"/>
                </radialGradient>
              </defs>
              {/* Shield shape */}
              <path
                d="M100 10 L170 40 L170 110 Q170 170 100 200 Q30 170 30 110 L30 40 Z"
                fill="url(#shieldGrad)"
                stroke="rgba(59,130,246,0.4)"
                strokeWidth="1.5"
                className="network-pulse"
              />
              {/* Inner shield */}
              <path
                d="M100 35 L150 58 L150 108 Q150 155 100 178 Q50 155 50 108 L50 58 Z"
                fill="rgba(37,99,235,0.08)"
                stroke="rgba(96,165,250,0.25)"
                strokeWidth="1"
              />
              {/* Check mark */}
              <path
                d="M75 105 L92 122 L128 86"
                fill="none"
                stroke={active?.color}
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transition: "stroke 0.4s ease" }}
              />
              {/* Orbiting dots */}
              {orbitingDots?.map((dot, i) => (
                <circle
                  key={i}
                  cx={dot?.cx} cy={dot?.cy} r="4"
                  fill={stages?.[i]?.color}
                  opacity={i === activeStage ? 1 : 0.3}
                  style={{ transition: "opacity 0.4s ease" }}
                />
              ))}
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}